"use client"

// src/ReactCompareSlider.tsx
import {
  forwardRef as forwardRef2,
  useCallback as useCallback2,
  useEffect as useEffect2,
  useImperativeHandle,
  useRef as useRef2,
  useState
} from "react";

// src/Container.tsx
import { forwardRef } from "react";
import { jsx } from "react/jsx-runtime";
var ContainerItem = forwardRef(
  ({ transition, order, ...props }, ref) => {
    const style = {
      gridArea: "1 / 1 / 2 / 2",
      order,
      maxWidth: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: transition ? `clip-path ${transition}` : void 0,
      userSelect: "none",
      willChange: "clip-path, transition",
      KhtmlUserSelect: "none",
      MozUserSelect: "none",
      WebkitUserSelect: "none"
    };
    return /* @__PURE__ */ jsx("div", { ...props, style, "data-rcs": "clip-item", ref });
  }
);
ContainerItem.displayName = "ContainerItem";
var ContainerHandle = forwardRef(
  ({ children, disabled, portrait, position, transition }, ref) => {
    const targetAxis = portrait ? "top" : "left";
    const style = {
      position: "absolute",
      top: 0,
      width: portrait ? "100%" : void 0,
      height: portrait ? void 0 : "100%",
      background: "none",
      border: 0,
      padding: 0,
      pointerEvents: "all",
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      outline: 0,
      transform: portrait ? `translate3d(0, -50% ,0)` : `translate3d(-50%, 0, 0)`,
      transition: transition ? `${targetAxis} ${transition}` : void 0
    };
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        "aria-label": "Drag to move or focus and use arrow keys",
        "aria-orientation": portrait ? "vertical" : "horizontal",
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": position,
        "data-rcs": "handle-container",
        disabled,
        role: "slider",
        style,
        children
      }
    );
  }
);
ContainerHandle.displayName = "ThisHandleContainer";

// src/ReactCompareSliderHandle.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var ThisArrow = ({ flip }) => {
  const style = {
    width: 0,
    height: 0,
    borderTop: "8px solid transparent",
    borderRight: "10px solid",
    borderBottom: "8px solid transparent",
    transform: flip ? "rotate(180deg)" : void 0
  };
  return /* @__PURE__ */ jsx2("div", { className: "__rcs-handle-arrow", style });
};
var ReactCompareSliderHandle = ({
  className = "__rcs-handle-root",
  disabled,
  buttonStyle,
  linesStyle,
  portrait,
  style,
  ...props
}) => {
  const _style = {
    display: "flex",
    flexDirection: portrait ? "row" : "column",
    placeItems: "center",
    height: "100%",
    cursor: disabled ? "not-allowed" : portrait ? "ns-resize" : "ew-resize",
    pointerEvents: "none",
    color: "#fff",
    ...style
  };
  const _linesStyle = {
    flexGrow: 1,
    height: portrait ? 2 : "100%",
    width: portrait ? "100%" : 2,
    backgroundColor: "currentColor",
    pointerEvents: "auto",
    boxShadow: "0 0 4px rgba(0,0,0,.5)",
    ...linesStyle
  };
  const _buttonStyle = {
    display: "grid",
    gridAutoFlow: "column",
    gap: 8,
    placeContent: "center",
    flexShrink: 0,
    width: 56,
    height: 56,
    borderRadius: "50%",
    borderStyle: "solid",
    borderWidth: 2,
    pointerEvents: "auto",
    backdropFilter: "blur(7px)",
    WebkitBackdropFilter: "blur(7px)",
    // For Safari.
    backgroundColor: "rgba(0, 0, 0, 0.125)",
    boxShadow: "0 0 4px rgba(0,0,0,.35)",
    transform: portrait ? "rotate(90deg)" : void 0,
    ...buttonStyle
  };
  return /* @__PURE__ */ jsxs("div", { ...props, className, style: _style, children: [
    /* @__PURE__ */ jsx2("div", { className: "__rcs-handle-line", style: _linesStyle }),
    /* @__PURE__ */ jsxs("div", { className: "__rcs-handle-button", style: _buttonStyle, children: [
      /* @__PURE__ */ jsx2(ThisArrow, {}),
      /* @__PURE__ */ jsx2(ThisArrow, { flip: true })
    ] }),
    /* @__PURE__ */ jsx2("div", { className: "__rcs-handle-line", style: _linesStyle })
  ] });
};

// src/types.ts
var ReactCompareSliderClipOption = {
  both: "both",
  itemOne: "itemOne",
  itemTwo: "itemTwo"
};

// src/utils.ts
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
var KeyboardEventKeys = /* @__PURE__ */ ((KeyboardEventKeys2) => {
  KeyboardEventKeys2["ARROW_LEFT"] = "ArrowLeft";
  KeyboardEventKeys2["ARROW_RIGHT"] = "ArrowRight";
  KeyboardEventKeys2["ARROW_UP"] = "ArrowUp";
  KeyboardEventKeys2["ARROW_DOWN"] = "ArrowDown";
  return KeyboardEventKeys2;
})(KeyboardEventKeys || {});
var styleFitContainer = ({
  boxSizing = "border-box",
  objectFit = "cover",
  objectPosition = "center center",
  ...props
} = {}) => ({
  display: "block",
  width: "100%",
  height: "100%",
  maxWidth: "100%",
  boxSizing,
  objectFit,
  objectPosition,
  ...props
});
var usePrevious = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
var useEventListener = (eventName, handler, element, handlerOptions) => {
  const savedHandler = useRef();
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(() => {
    if (!(element && element.addEventListener)) return;
    const eventListener = (event) => savedHandler.current && savedHandler.current(event);
    element.addEventListener(eventName, eventListener, handlerOptions);
    return () => {
      element.removeEventListener(eventName, eventListener, handlerOptions);
    };
  }, [eventName, element, handlerOptions]);
};
var useIsomorphicLayoutEffect = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined" ? useLayoutEffect : useEffect;
var useResizeObserver = (ref, handler) => {
  const observer = useRef();
  const observe = useCallback(() => {
    if (ref.current && observer.current) observer.current.observe(ref.current);
  }, [ref]);
  useIsomorphicLayoutEffect(() => {
    observer.current = new ResizeObserver(([entry]) => handler(entry.contentRect));
    observe();
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [handler, observe]);
};

// src/ReactCompareSlider.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var EVENT_PASSIVE_PARAMS = { capture: false, passive: true };
var EVENT_CAPTURE_PARAMS = { capture: true, passive: false };
var handleContainerClick = (ev) => {
  ev.preventDefault();
  ev.currentTarget.focus();
};
var ReactCompareSlider = forwardRef2(
  ({
    boundsPadding = 0,
    browsingContext = globalThis,
    changePositionOnHover = false,
    clip = ReactCompareSliderClipOption.both,
    disabled = false,
    handle,
    itemOne,
    itemTwo,
    keyboardIncrement = "5%",
    onlyHandleDraggable = false,
    onPositionChange,
    portrait = false,
    position = 50,
    style,
    transition,
    ...props
  }, ref) => {
    const rootContainerRef = useRef2(null);
    const clipContainerOneRef = useRef2(null);
    const clipContainerTwoRef = useRef2(null);
    const handleContainerRef = useRef2(null);
    const internalPosition = useRef2(position);
    const [isDragging, setIsDragging] = useState(false);
    const [canTransition, setCanTransition] = useState(true);
    const hasBrowsingContextBinding = useRef2(false);
    const [interactiveTarget, setInteractiveTarget] = useState();
    const previousPosition = usePrevious(position);
    const updateInternalPosition = useCallback2(
      function updateInternal({ x, y, isOffset }) {
        const rootElement = rootContainerRef.current;
        const handleElement = handleContainerRef.current;
        const clipElementOne = clipContainerOneRef.current;
        const clipElementTwo = clipContainerTwoRef.current;
        const { width, height, left, top } = rootElement.getBoundingClientRect();
        if (width === 0 || height === 0) {
          return;
        }
        const pixelPosition = portrait ? isOffset ? y - top - browsingContext.scrollY : y : isOffset ? x - left - browsingContext.scrollX : x;
        const nextPosition = Math.min(
          Math.max(pixelPosition / (portrait ? height : width) * 100, 0),
          100
        );
        const zoomScale = portrait ? height / (rootElement.offsetHeight || 1) : width / (rootElement.offsetWidth || 1);
        const boundsPaddingPercentage = boundsPadding * zoomScale / (portrait ? height : width) * 100;
        const nextPositionWithBoundsPadding = Math.min(
          Math.max(nextPosition, boundsPaddingPercentage * zoomScale),
          100 - boundsPaddingPercentage * zoomScale
        );
        internalPosition.current = nextPosition;
        handleElement.setAttribute("aria-valuenow", `${Math.round(internalPosition.current)}`);
        handleElement.style.top = portrait ? `${nextPositionWithBoundsPadding}%` : "0";
        handleElement.style.left = portrait ? "0" : `${nextPositionWithBoundsPadding}%`;
        const clipBoth = clip === ReactCompareSliderClipOption.both;
        if (clipBoth || clip === ReactCompareSliderClipOption.itemOne) {
          clipElementOne.style.clipPath = portrait ? `inset(0 0 ${100 - nextPositionWithBoundsPadding}% 0)` : `inset(0 ${100 - nextPositionWithBoundsPadding}% 0 0)`;
        } else {
          clipElementOne.style.clipPath = "none";
        }
        if (clipBoth || clip === ReactCompareSliderClipOption.itemTwo) {
          clipElementTwo.style.clipPath = portrait ? `inset(${nextPositionWithBoundsPadding}% 0 0 0)` : `inset(0 0 0 ${nextPositionWithBoundsPadding}%)`;
        } else {
          clipElementTwo.style.clipPath = "none";
        }
        if (onPositionChange) {
          onPositionChange(internalPosition.current);
        }
      },
      [browsingContext, boundsPadding, clip, onPositionChange, portrait]
    );
    useEffect2(() => {
      const { width, height } = rootContainerRef.current.getBoundingClientRect();
      const nextPosition = position === previousPosition ? internalPosition.current : position;
      updateInternalPosition({
        x: width / 100 * nextPosition,
        y: height / 100 * nextPosition
      });
    }, [boundsPadding, clip, position, portrait, previousPosition, updateInternalPosition]);
    const handlePointerDown = useCallback2(
      (ev) => {
        ev.preventDefault();
        if (disabled || ev.button !== 0) return;
        updateInternalPosition({ isOffset: true, x: ev.pageX, y: ev.pageY });
        setIsDragging(true);
        setCanTransition(true);
      },
      [disabled, updateInternalPosition]
    );
    const handlePointerMove = useCallback2(
      function moveCall(ev) {
        updateInternalPosition({ isOffset: true, x: ev.pageX, y: ev.pageY });
        setCanTransition(false);
      },
      [updateInternalPosition]
    );
    const handlePointerUp = useCallback2(() => {
      setIsDragging(false);
      setCanTransition(true);
    }, []);
    const handleTouchEnd = useCallback2(() => {
      setIsDragging(false);
      setCanTransition(true);
    }, []);
    const handleResize = useCallback2(
      ({ width, height }) => {
        const { width: scaledWidth, height: scaledHeight } = rootContainerRef.current.getBoundingClientRect();
        updateInternalPosition({
          x: width / 100 * internalPosition.current * scaledWidth / width,
          y: height / 100 * internalPosition.current * scaledHeight / height
        });
      },
      [updateInternalPosition]
    );
    const handleKeydown = useCallback2(
      (ev) => {
        if (!Object.values(KeyboardEventKeys).includes(ev.key)) {
          return;
        }
        ev.preventDefault();
        setCanTransition(true);
        const { top, left } = handleContainerRef.current.getBoundingClientRect();
        const { width, height } = rootContainerRef.current.getBoundingClientRect();
        const isPercentage = typeof keyboardIncrement === "string";
        const incrementPercentage = isPercentage ? parseFloat(keyboardIncrement) : keyboardIncrement / width * 100;
        const isIncrement = portrait ? ev.key === "ArrowLeft" /* ARROW_LEFT */ || ev.key === "ArrowDown" /* ARROW_DOWN */ : ev.key === "ArrowRight" /* ARROW_RIGHT */ || ev.key === "ArrowUp" /* ARROW_UP */;
        const nextPosition = Math.min(
          Math.max(
            isIncrement ? internalPosition.current + incrementPercentage : internalPosition.current - incrementPercentage,
            0
          ),
          100
        );
        updateInternalPosition({
          x: portrait ? left : width * nextPosition / 100,
          y: portrait ? height * nextPosition / 100 : top
        });
      },
      [keyboardIncrement, portrait, updateInternalPosition]
    );
    useEffect2(() => {
      setInteractiveTarget(
        onlyHandleDraggable ? handleContainerRef.current : rootContainerRef.current
      );
    }, [onlyHandleDraggable]);
    useEffect2(() => {
      const containerRef = rootContainerRef.current;
      const handlePointerLeave = () => {
        if (isDragging) return;
        handlePointerUp();
      };
      if (changePositionOnHover) {
        containerRef.addEventListener("pointermove", handlePointerMove, EVENT_PASSIVE_PARAMS);
        containerRef.addEventListener("pointerleave", handlePointerLeave, EVENT_PASSIVE_PARAMS);
      }
      return () => {
        containerRef.removeEventListener("pointermove", handlePointerMove);
        containerRef.removeEventListener("pointerleave", handlePointerLeave);
      };
    }, [changePositionOnHover, handlePointerMove, handlePointerUp, isDragging]);
    useEffect2(() => {
      if (isDragging && !hasBrowsingContextBinding.current) {
        browsingContext.addEventListener("pointermove", handlePointerMove, EVENT_PASSIVE_PARAMS);
        browsingContext.addEventListener("pointerup", handlePointerUp, EVENT_PASSIVE_PARAMS);
        hasBrowsingContextBinding.current = true;
      }
      return () => {
        if (hasBrowsingContextBinding.current) {
          browsingContext.removeEventListener("pointermove", handlePointerMove);
          browsingContext.removeEventListener("pointerup", handlePointerUp);
          hasBrowsingContextBinding.current = false;
        }
      };
    }, [handlePointerMove, handlePointerUp, isDragging, browsingContext]);
    useImperativeHandle(
      ref,
      () => {
        return {
          rootContainer: rootContainerRef.current,
          handleContainer: handleContainerRef.current,
          setPosition(nextPosition) {
            const { width, height } = rootContainerRef.current.getBoundingClientRect();
            updateInternalPosition({
              x: width / 100 * nextPosition,
              y: height / 100 * nextPosition
            });
          }
        };
      },
      [updateInternalPosition]
    );
    useResizeObserver(rootContainerRef, handleResize);
    useEventListener(
      "touchend",
      handleTouchEnd,
      interactiveTarget,
      EVENT_CAPTURE_PARAMS
    );
    useEventListener(
      "keydown",
      handleKeydown,
      handleContainerRef.current,
      EVENT_CAPTURE_PARAMS
    );
    useEventListener(
      "click",
      handleContainerClick,
      handleContainerRef.current,
      EVENT_CAPTURE_PARAMS
    );
    useEventListener(
      "pointerdown",
      handlePointerDown,
      interactiveTarget,
      EVENT_CAPTURE_PARAMS
    );
    const Handle = handle || /* @__PURE__ */ jsx3(ReactCompareSliderHandle, { disabled, portrait });
    const appliedTransition = canTransition ? transition : void 0;
    const rootStyle = {
      position: "relative",
      display: "grid",
      maxWidth: "100%",
      maxHeight: "100%",
      overflow: "hidden",
      cursor: isDragging ? portrait ? "ns-resize" : "ew-resize" : void 0,
      touchAction: "pan-y",
      userSelect: "none",
      KhtmlUserSelect: "none",
      msUserSelect: "none",
      MozUserSelect: "none",
      WebkitUserSelect: "none",
      ...style
    };
    return /* @__PURE__ */ jsxs2("div", { ...props, ref: rootContainerRef, style: rootStyle, "data-rcs": "root", children: [
      /* @__PURE__ */ jsx3(ContainerItem, { ref: clipContainerOneRef, transition: appliedTransition, children: itemOne }),
      /* @__PURE__ */ jsx3(ContainerItem, { ref: clipContainerTwoRef, transition: appliedTransition, children: itemTwo }),
      /* @__PURE__ */ jsx3(
        ContainerHandle,
        {
          disabled,
          portrait,
          position: Math.round(internalPosition.current),
          ref: handleContainerRef,
          transition: appliedTransition,
          children: Handle
        }
      )
    ] });
  }
);
ReactCompareSlider.displayName = "ReactCompareSlider";

// src/ReactCompareSliderImage.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var ReactCompareSliderImage = forwardRef3(
  ({ style, ...props }, ref) => {
    const rootStyle = styleFitContainer(style);
    return /* @__PURE__ */ jsx4("img", { ref, ...props, style: rootStyle, "data-rcs": "image" });
  }
);
ReactCompareSliderImage.displayName = "ReactCompareSliderImage";

// src/useReactCompareSliderRef.ts
import { useRef as useRef3 } from "react";
var useReactCompareSliderRef = () => useRef3({
  rootContainer: null,
  handleContainer: null,
  setPosition: () => (
    // eslint-disable-next-line no-console
    console.warn(
      "[react-compare-slider] `setPosition` cannot be used until the component has mounted."
    )
  )
});
export {
  ReactCompareSlider,
  ReactCompareSliderClipOption,
  ReactCompareSliderHandle,
  ReactCompareSliderImage,
  styleFitContainer,
  useReactCompareSliderRef
};
//# sourceMappingURL=index.mjs.map